// DrawingRegisterSection.js
// ─────────────────────────────────────────────────────────────────────────────
// CONTROLLED DRAWING / DOCUMENT REGISTER
//
// The controlled source of truth behind every drawing number quoted on RFIs,
// site instructions and NCRs. One row per drawing; a full revision history per
// drawing; transmittal references on each revision; automatic "Superseded"
// status once a newer revision is issued.
//
// Firestore: projects/{projectId}/drawings — one doc per drawing.
//   {
//     drawingNo, title, discipline, scale, sheetSize, originator,
//     issueStatus,          // For Construction / For Approval / For Information / Void
//     revisions: [ { rev, revDate, description, transmittalRef, transmittalDate,
//                    receivedDate, issueStatus } ],
//   }
// The CURRENT revision is the last entry in revisions[] (highest by issue date);
// every earlier revision is, by definition, Superseded.
//
// Self-contained: reads/writes its own collection, takes only the project.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, setDoc, getDocs, writeBatch,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";

const C = tokens.color;
const MONO = tokens.font?.mono || '"JetBrains Mono", monospace';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

const DISCIPLINES = ["Civil", "Structural", "Mechanical", "Electrical", "Architectural",
  "Geotechnical", "Survey", "Process", "General"];
const ISSUE_STATUSES = ["For Construction", "For Approval", "For Information", "As-Built", "Void"];

const todayISO = () => new Date().toISOString().slice(0, 10);

// Increment a revision string: numeric (0→1, 09→10), trailing number
// (P1→P2, C3→C4) or trailing letter (A→B, Z→ZA).
function bumpRev(rev) {
  const r = String(rev ?? "").trim();
  if (r === "") return "1";
  const num = r.match(/^(.*?)(\d+)\s*$/);
  if (num) {
    const width = num[2].length;
    return num[1] + String(Number(num[2]) + 1).padStart(width, "0");
  }
  const alpha = r.match(/^(.*?)([A-Za-z])\s*$/);
  if (alpha) {
    const ch = alpha[2];
    if (ch.toUpperCase() !== "Z") return alpha[1] + String.fromCharCode(ch.charCodeAt(0) + 1);
    return alpha[1] + ch + "A";
  }
  return r + "1";
}

// Flexible spreadsheet column matching: exact header first, then "contains".
function pickField(row, exact, contains) {
  const keys = Object.keys(row);
  for (const a of exact) {
    const k = keys.find((key) => key.trim().toLowerCase() === a);
    if (k && String(row[k]).trim() !== "") return row[k];
  }
  for (const a of contains) {
    const k = keys.find((key) => key.trim().toLowerCase().includes(a));
    if (k && String(row[k]).trim() !== "") return row[k];
  }
  return "";
}
const COL = {
  drawingNo:  { exact: ["drawing no", "drawing number", "dwg no", "dwg number", "number", "no", "no.", "dwg"], contains: ["drawing no", "dwg", "number"] },
  title:      { exact: ["title", "name", "description", "drawing title", "drawing name"], contains: ["title", "name", "descr"] },
  rev:        { exact: ["rev", "revision", "current rev", "current revision", "rev no", "revno"], contains: ["rev"] },
  discipline: { exact: ["discipline", "disc"], contains: ["discipl"] },
};

const PULSE_CSS =
  `@keyframes dwgPulse { 0%, 100% { background: transparent; } 50% { background: ${C.dangerSoft}; } }`;

// The current (latest) revision: highest revDate, then last in array.
function currentRevision(dwg) {
  const revs = Array.isArray(dwg.revisions) ? dwg.revisions : [];
  if (!revs.length) return null;
  return [...revs].sort((a, b) =>
    String(a.revDate || "").localeCompare(String(b.revDate || "")) ||
    String(a.rev || "").localeCompare(String(b.rev || ""), undefined, { numeric: true })
  ).slice(-1)[0];
}

function drawingStatus(dwg) {
  const cur = currentRevision(dwg);
  if ((dwg.issueStatus || cur?.issueStatus) === "Void") return "Void";
  if (!cur) return "No Revision";
  if ((cur.issueStatus || dwg.issueStatus) === "As-Built") return "As-Built";
  return "Current";
}

const STATUS_META = {
  "Current":     { bg: C.successSoft, text: C.success, border: C.successBorder },
  "As-Built":    { bg: C.infoSoft,    text: C.info,    border: C.infoBorder },
  "Superseded":  { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  "Void":        { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder },
  "No Revision": { bg: C.neutralSoft, text: C.neutral, border: C.neutralBorder },
};

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 12px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none" },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnPrimary: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.primary, color: C.inkOnDark || "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnSmall: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: "nowrap" }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 20, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
};

const blankDrawing = () => ({
  drawingNo: "", title: "", discipline: "Civil", scale: "", sheetSize: "A1",
  originator: "", issueStatus: "For Construction", revisions: [],
});

// ─── Revision history editor (portal modal) ──────────────────────────────────

function RevisionModal({ drawing, onSave, onClose }) {
  const [rows, setRows] = useState(() =>
    (drawing.revisions || []).map((r) => ({ ...r, _id: r._id || `r_${Math.random().toString(36).slice(2)}` }))
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, {
    _id: `r_${Date.now().toString(36)}`, rev: rs.length ? "" : "0",
    revDate: new Date().toISOString().slice(0, 10), description: "",
    transmittalRef: "", transmittalDate: "", receivedDate: "", issueStatus: "For Construction",
  }]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r._id !== id));

  const ordered = [...rows].sort((a, b) =>
    String(a.revDate || "").localeCompare(String(b.revDate || "")) ||
    String(a.rev || "").localeCompare(String(b.rev || ""), undefined, { numeric: true })
  );
  const latestId = ordered.length ? ordered[ordered.length - 1]._id : null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.surface, borderRadius: 14, width: "min(860px, 100%)", maxHeight: "88vh", overflowY: "auto", padding: 22, boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          <div>
            <h3 style={{ ...s.title, fontSize: 18 }}>Revision history — {drawing.drawingNo || "drawing"}</h3>
            <p style={s.sub}>The latest revision is Current; every earlier revision is Superseded automatically.</p>
          </div>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Rev", "Rev date", "Description", "Transmittal", "Transmittal date", "Received", "Issue status", ""].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {ordered.map((r) => {
                const superseded = r._id !== latestId;
                return (
                  <tr key={r._id} style={superseded ? { opacity: 0.62 } : null}>
                    <td style={s.td}><input style={{ ...s.inp, width: 64 }} value={r.rev} onChange={(e) => setRow(r._id, { rev: e.target.value })} /></td>
                    <td style={s.td}><input type="date" style={{ ...s.inp, width: 150 }} value={r.revDate} onChange={(e) => setRow(r._id, { revDate: e.target.value })} /></td>
                    <td style={s.td}><input style={{ ...s.inp, minWidth: 180 }} value={r.description} onChange={(e) => setRow(r._id, { description: e.target.value })} placeholder="Issued for…" /></td>
                    <td style={s.td}><input style={{ ...s.inp, width: 120 }} value={r.transmittalRef} onChange={(e) => setRow(r._id, { transmittalRef: e.target.value })} placeholder="TR-001" /></td>
                    <td style={s.td}><input type="date" style={{ ...s.inp, width: 150 }} value={r.transmittalDate} onChange={(e) => setRow(r._id, { transmittalDate: e.target.value })} /></td>
                    <td style={s.td}><input type="date" style={{ ...s.inp, width: 150 }} value={r.receivedDate} onChange={(e) => setRow(r._id, { receivedDate: e.target.value })} /></td>
                    <td style={s.td}>
                      <select style={{ ...s.inp, width: 150 }} value={r.issueStatus} onChange={(e) => setRow(r._id, { issueStatus: e.target.value })}>
                        {ISSUE_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                      {!superseded
                        ? <div style={{ ...s.chip(STATUS_META.Current), marginTop: 4 }}>Current</div>
                        : <div style={{ ...s.chip(STATUS_META.Superseded), marginTop: 4 }}>Superseded</div>}
                    </td>
                    <td style={s.td}><button style={s.btnDanger} onClick={() => removeRow(r._id)}>Remove</button></td>
                  </tr>
                );
              })}
              {!ordered.length && (
                <tr><td style={{ ...s.td, color: C.inkMuted }} colSpan={8}>No revisions captured yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button style={s.btn} onClick={addRow}>+ Add revision</button>
          <div style={{ flex: 1 }} />
          <button style={s.btn} onClick={onClose}>Cancel</button>
          <button style={s.btnAccent} onClick={() => onSave(rows.map(({ _id, ...rest }) => rest))}>Save revisions</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Excel import preview (portal modal) ─────────────────────────────────────

function ImportPreviewModal({ rows, existingKeys, busy, onImport, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const newCount = rows.filter((r) => !existingKeys.has(r.drawingNo.toLowerCase())).length;
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.surface, borderRadius: 14, width: "min(760px, 100%)", maxHeight: "88vh", overflowY: "auto", padding: 22, boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
          <div>
            <h3 style={{ ...s.title, fontSize: 18 }}>Import drawings — {rows.length} row(s)</h3>
            <p style={s.sub}>{newCount} new · {rows.length - newCount} already in the register (will update name only).</p>
          </div>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Drawing No", "Name / title", "Current rev", "Discipline", ""].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const isNew = !existingKeys.has(r.drawingNo.toLowerCase());
                return (
                  <tr key={`${r.drawingNo}_${i}`}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 700 }}>{r.drawingNo}</td>
                    <td style={s.td}>{r.title || "—"}</td>
                    <td style={{ ...s.td, ...s.mono }}>{r.rev || "—"}</td>
                    <td style={s.td}>{r.discipline || "—"}</td>
                    <td style={s.td}><span style={s.chip(isNew ? STATUS_META.Current : STATUS_META.Superseded)}>{isNew ? "new" : "update"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button style={s.btn} onClick={onClose}>Cancel</button>
          <button style={s.btnAccent} onClick={onImport} disabled={busy}>{busy ? "Importing…" : `Import ${rows.length} drawing(s)`}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── "New revision detected → generate SI?" (portal modal) ───────────────────

function GenerateSiModal({ drawing, busy, onYes, onNo, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.surface, borderRadius: 14, width: "min(520px, 100%)", padding: 24, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", borderTop: `4px solid ${C.danger}` }}>
        <h3 style={{ ...s.title, fontSize: 18, marginBottom: 6 }}>New revision detected — {drawing.drawingNo}</h3>
        <p style={{ ...s.sub, fontSize: 13 }}>
          {drawing.drawingNo} has been revised to <b style={{ color: C.ink }}>Rev {drawing.pendingRevValue}</b>
          {drawing.pendingFromValue !== "" ? <> (was Rev {drawing.pendingFromValue})</> : null}. Generate a Site
          Instruction to issue this revision to the contractor?
        </p>
        <p style={{ ...s.sub, fontSize: 12, marginTop: 10, color: C.warning }}>
          Choosing “No” reverts the drawing to Rev {drawing.pendingFromValue || "—"}.
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
          <button style={{ ...s.btn, borderColor: C.dangerBorder, color: C.danger }} onClick={onNo} disabled={busy}>No — revert revision</button>
          <button style={s.btnAccent} onClick={onYes} disabled={busy}>{busy ? "Creating…" : "Yes — generate SI"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
function DrawingRegisterSection({ currentProject, currentProjectId }) {
  const projectId = currentProject?.id || currentProjectId;

  const [drawings, setDrawings] = useState([]);
  const [form, setForm] = useState(blankDrawing);
  const [editingId, setEditingId] = useState(null);
  const [revModalId, setRevModalId] = useState(null);
  const [filterDisc, setFilterDisc] = useState("");
  const [search, setSearch] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importBusy, setImportBusy] = useState(false);
  const [genSiFor, setGenSiFor] = useState(null);
  const [siBusy, setSiBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!projectId) { setDrawings([]); return undefined; }
    const q = query(collection(db, "projects", projectId, "drawings"), orderBy("drawingNo"));
    const unsub = onSnapshot(q,
      (snap) => setDrawings(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("drawings listener:", err)
    );
    return unsub;
  }, [projectId]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return drawings.filter((d) => {
      if (filterDisc && d.discipline !== filterDisc) return false;
      if (needle) {
        const hay = `${d.drawingNo} ${d.title} ${d.originator}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [drawings, filterDisc, search]);

  const kpis = useMemo(() => {
    let current = 0, superseded = 0, voids = 0;
    drawings.forEach((d) => {
      const st = drawingStatus(d);
      if (st === "Void") voids += 1;
      else if (st === "Current" || st === "As-Built") current += 1;
      superseded += Math.max(0, (Array.isArray(d.revisions) ? d.revisions.length : 0) - 1);
    });
    return { total: drawings.length, current, superseded, voids };
  }, [drawings]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function resetForm() { setForm(blankDrawing()); setEditingId(null); }

  async function saveDrawing() {
    if (!projectId) return;
    if (!form.drawingNo.trim()) { alert("Drawing number is required."); return; }
    const payload = {
      drawingNo: form.drawingNo.trim(), title: form.title.trim(),
      discipline: form.discipline, scale: form.scale, sheetSize: form.sheetSize,
      originator: form.originator, issueStatus: form.issueStatus,
      revisions: Array.isArray(form.revisions) ? form.revisions : [],
      updatedAt: serverTimestamp(),
    };
    try {
      if (editingId) {
        await updateDoc(doc(db, "projects", projectId, "drawings", editingId), payload);
      } else {
        await addDoc(collection(db, "projects", projectId, "drawings"), { ...payload, createdAt: serverTimestamp() });
      }
      resetForm();
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
  }

  function editDrawing(d) {
    setForm({
      drawingNo: d.drawingNo || "", title: d.title || "", discipline: d.discipline || "Civil",
      scale: d.scale || "", sheetSize: d.sheetSize || "A1", originator: d.originator || "",
      issueStatus: d.issueStatus || "For Construction", revisions: d.revisions || [],
    });
    setEditingId(d.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeDrawing(id) {
    if (!projectId) return;
    if (!window.confirm("Delete this drawing and its revision history?")) return;
    try { await deleteDoc(doc(db, "projects", projectId, "drawings", id)); }
    catch (e) { alert(`Delete failed: ${e.message || e}`); }
  }

  async function saveRevisions(revisions) {
    if (!projectId || !revModalId) return;
    try {
      await updateDoc(doc(db, "projects", projectId, "drawings", revModalId), { revisions, updatedAt: serverTimestamp() });
      setRevModalId(null);
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
  }

  // ── Excel import ──────────────────────────────────────────────────────────
  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";   // allow re-importing the same file
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) { alert("That workbook has no sheets."); return; }
      const raw = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      const parsed = raw.map((row) => ({
        drawingNo: String(pickField(row, COL.drawingNo.exact, COL.drawingNo.contains)).trim(),
        title: String(pickField(row, COL.title.exact, COL.title.contains)).trim(),
        rev: String(pickField(row, COL.rev.exact, COL.rev.contains)).trim(),
        discipline: String(pickField(row, COL.discipline.exact, COL.discipline.contains)).trim(),
      })).filter((r) => r.drawingNo);
      if (!parsed.length) {
        alert("No drawing rows found. Expected columns for drawing number, name and current revision.");
        return;
      }
      setImportRows(parsed);
    } catch (err) { alert(`Could not read file: ${err.message || err}`); }
  }

  async function doImport() {
    if (!projectId || !importRows.length) return;
    setImportBusy(true);
    try {
      const existing = new Map(drawings.map((d) => [String(d.drawingNo).trim().toLowerCase(), d]));
      const batch = writeBatch(db);
      let created = 0, updated = 0;
      for (const r of importRows) {
        const found = existing.get(r.drawingNo.toLowerCase());
        if (found) {
          batch.update(doc(db, "projects", projectId, "drawings", found.id), {
            title: r.title || found.title || "",
            discipline: r.discipline || found.discipline || "Civil",
            updatedAt: serverTimestamp(),
          });
          updated += 1;
        } else {
          const ref = doc(collection(db, "projects", projectId, "drawings"));
          batch.set(ref, {
            drawingNo: r.drawingNo, title: r.title, discipline: r.discipline || "Civil",
            scale: "", sheetSize: "A1", originator: "", issueStatus: "For Construction",
            revisions: [{
              rev: r.rev || "0", revDate: "", description: "Imported", transmittalRef: "",
              transmittalDate: "", receivedDate: "", issueStatus: "For Construction",
            }],
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          });
          created += 1;
        }
      }
      await batch.commit();
      setImportRows([]);
      alert(`Imported ${created} new drawing(s); updated ${updated}.`);
    } catch (e) { alert(`Import failed: ${e.message || e}`); }
    finally { setImportBusy(false); }
  }

  // ── Rev up / revert / generate SI ─────────────────────────────────────────
  async function revUp(d) {
    if (!projectId || d.pendingRev) return;
    const cur = currentRevision(d);
    const fromRev = cur?.rev ?? "";
    const newRev = bumpRev(fromRev);
    const newEntry = {
      rev: newRev, revDate: todayISO(), description: "Revision up",
      transmittalRef: "", transmittalDate: "", receivedDate: "",
      issueStatus: cur?.issueStatus || d.issueStatus || "For Construction",
    };
    const revisions = [...(Array.isArray(d.revisions) ? d.revisions : []), newEntry];
    try {
      await updateDoc(doc(db, "projects", projectId, "drawings", d.id), {
        revisions, pendingRev: true, pendingFromValue: fromRev, pendingRevValue: newRev,
        updatedAt: serverTimestamp(),
      });
    } catch (e) { alert(`Rev up failed: ${e.message || e}`); }
  }

  async function revertRev(d) {
    if (!projectId) return;
    setSiBusy(true);
    const revisions = (Array.isArray(d.revisions) ? d.revisions : []).slice(0, -1);
    try {
      await updateDoc(doc(db, "projects", projectId, "drawings", d.id), {
        revisions, pendingRev: false, pendingFromValue: "", pendingRevValue: "",
        updatedAt: serverTimestamp(),
      });
      setGenSiFor(null);
    } catch (e) { alert(`Revert failed: ${e.message || e}`); }
    finally { setSiBusy(false); }
  }

  async function generateSI(d) {
    if (!projectId) return;
    setSiBusy(true);
    const newRev = d.pendingRevValue || currentRevision(d)?.rev || "";
    try {
      // next SI number from the existing instructions register
      let siNumber = "SI-001";
      const snap = await getDocs(collection(db, "projects", projectId, "instructions"));
      const nums = snap.docs
        .map((x) => parseInt(String(x.data().siNumber || x.id).replace(/[^0-9]/g, ""), 10))
        .filter(Number.isFinite);
      siNumber = `SI-${String(nums.length ? Math.max(...nums) + 1 : 1).padStart(3, "0")}`;
      const siId = siNumber.replace(/\//g, "-");
      const p = currentProject || {};
      const payload = {
        id: siId, siNumber, projectNumber: projectId, projectId,
        contractNumber: p.contractNumber || "", contractDescription: p.name || p.contractDescription || "",
        contractor: p.contractor || "", issuedBy: "",
        subject: `Latest revision of drawing ${d.drawingNo}`,
        instructionText: `Herewith latest revision of drawing ${d.drawingNo}${d.title ? ` (${d.title})` : ""} — Revision ${newRev}.`,
        drawingRevisionRef: `${d.drawingNo} Rev ${newRev}`,
        financialImplications: "No", instructionDate: todayISO(), instructionTime: "",
        asBuiltImplications: "", valuationBasis: "", applicablePayItems: "",
        distribution: "", relatedRfiId: "", relatedVariationId: "",
        createdVia: "drawingRegister",
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      };
      await setDoc(doc(db, "projects", projectId, "instructions", siId), payload, { merge: true });
      await updateDoc(doc(db, "projects", projectId, "drawings", d.id), {
        pendingRev: false, pendingFromValue: "", pendingRevValue: "",
        lastSiRef: siNumber, updatedAt: serverTimestamp(),
      });
      setGenSiFor(null);
      alert(`Site instruction ${siNumber} created for ${d.drawingNo} Rev ${newRev}.`);
    } catch (e) { alert(`Could not create SI: ${e.message || e}`); }
    finally { setSiBusy(false); }
  }

  const revModalDrawing = drawings.find((d) => d.id === revModalId);

  if (!projectId) {
    return <div style={s.page}><div style={s.card}><p style={s.sub}>Select a project to view its drawing register.</p></div></div>;
  }

  return (
    <div style={s.page}>
      <style>{PULSE_CSS}</style>
      <div style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div>
          <h1 style={s.title}>Drawing & Document Register</h1>
          <p style={s.sub}>Controlled register of drawing revisions, transmittals and superseded status — the source of truth behind every drawing number quoted on RFIs, instructions and NCRs.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleImportFile} />
          <button style={s.btn} onClick={() => fileRef.current && fileRef.current.click()}>⤓ Import from Excel</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <div style={s.statTile}><p style={s.statLabel}>Drawings</p><p style={s.statValue}>{kpis.total}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Current / As-Built</p><p style={{ ...s.statValue, color: C.success }}>{kpis.current}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Superseded revisions</p><p style={{ ...s.statValue, color: C.warning }}>{kpis.superseded}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Void</p><p style={{ ...s.statValue, color: C.danger }}>{kpis.voids}</p></div>
      </div>

      {/* ── Add / edit form ── */}
      <div style={s.card}>
        <h3 style={s.h3}>{editingId ? "Edit drawing" : "Add drawing"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div><label style={s.lbl}>Drawing No</label><input style={s.inp} value={form.drawingNo} onChange={(e) => setField("drawingNo", e.target.value)} placeholder="C-101" /></div>
          <div style={{ gridColumn: "span 2" }}><label style={s.lbl}>Title</label><input style={s.inp} value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="General arrangement — reservoir" /></div>
          <div><label style={s.lbl}>Discipline</label><select style={s.inp} value={form.discipline} onChange={(e) => setField("discipline", e.target.value)}>{DISCIPLINES.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          <div><label style={s.lbl}>Scale</label><input style={s.inp} value={form.scale} onChange={(e) => setField("scale", e.target.value)} placeholder="1:100" /></div>
          <div><label style={s.lbl}>Sheet size</label><select style={s.inp} value={form.sheetSize} onChange={(e) => setField("sheetSize", e.target.value)}>{["A0", "A1", "A2", "A3", "A4"].map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          <div><label style={s.lbl}>Originator</label><input style={s.inp} value={form.originator} onChange={(e) => setField("originator", e.target.value)} placeholder="Design consultant" /></div>
          <div><label style={s.lbl}>Issue status</label><select style={s.inp} value={form.issueStatus} onChange={(e) => setField("issueStatus", e.target.value)}>{ISSUE_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button style={s.btnAccent} onClick={saveDrawing}>{editingId ? "Update drawing" : "Add drawing"}</button>
          {editingId && <button style={s.btn} onClick={resetForm}>Cancel</button>}
          {editingId && <span style={{ ...s.sub, alignSelf: "center" }}>Use “Revisions” in the table to manage the revision history & transmittals.</span>}
        </div>
      </div>

      {/* ── Register ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <h3 style={{ ...s.h3, margin: 0 }}>Register</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ ...s.inp, width: 200 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search no / title / originator" />
            <select style={{ ...s.inp, width: 150 }} value={filterDisc} onChange={(e) => setFilterDisc(e.target.value)}>
              <option value="">All disciplines</option>
              {DISCIPLINES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Drawing No", "Title", "Discipline", "Current rev", "Rev date", "Transmittal", "Status", "Revs", "Actions"].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((d) => {
                const cur = currentRevision(d);
                const st = drawingStatus(d);
                const meta = STATUS_META[st] || STATUS_META["No Revision"];
                return (
                  <tr key={d.id} style={d.pendingRev ? { animation: "dwgPulse 1.1s ease-in-out infinite" } : null}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 700 }}>{d.drawingNo || "—"}</td>
                    <td style={s.td}>{d.title || "—"}</td>
                    <td style={s.td}>{d.discipline || "—"}</td>
                    <td style={{ ...s.td, ...s.mono, fontWeight: d.pendingRev ? 800 : 500, color: d.pendingRev ? C.danger : C.ink }}>
                      {cur?.rev ?? "—"}{d.pendingRev ? " ▲" : ""}
                    </td>
                    <td style={{ ...s.td, ...s.mono }}>{fmtDate(cur?.revDate)}</td>
                    <td style={{ ...s.td, ...s.mono }}>{cur?.transmittalRef || "—"}</td>
                    <td style={s.td}>
                      {d.pendingRev
                        ? <span style={s.chip(STATUS_META.Void)}>New revision</span>
                        : <span style={s.chip(meta)}>{st}</span>}
                    </td>
                    <td style={{ ...s.td, ...s.mono }}>{Array.isArray(d.revisions) ? d.revisions.length : 0}</td>
                    <td style={s.td}>
                      <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                        {d.pendingRev ? (
                          <button
                            style={{ ...s.btnSmall, borderColor: C.dangerBorder, background: C.dangerSoft, color: C.danger, fontWeight: 800 }}
                            onClick={() => setGenSiFor(d)}
                          >
                            ● Generate SI
                          </button>
                        ) : (
                          <button style={{ ...s.btnSmall, borderColor: C.accent, color: C.accent, fontWeight: 800 }} onClick={() => revUp(d)}>▲ Rev up</button>
                        )}
                        <button style={s.btnSmall} onClick={() => setRevModalId(d.id)}>Revisions</button>
                        <button style={s.btnSmall} onClick={() => editDrawing(d)}>Edit</button>
                        <button style={s.btnDanger} onClick={() => removeDrawing(d.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td style={{ ...s.td, color: C.inkMuted }} colSpan={9}>No drawings yet. Add the first drawing above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {revModalDrawing && (
        <RevisionModal drawing={revModalDrawing} onSave={saveRevisions} onClose={() => setRevModalId(null)} />
      )}

      {importRows.length > 0 && (
        <ImportPreviewModal
          rows={importRows}
          existingKeys={new Set(drawings.map((d) => String(d.drawingNo).trim().toLowerCase()))}
          busy={importBusy}
          onImport={doImport}
          onClose={() => setImportRows([])}
        />
      )}

      {genSiFor && (() => {
        const live = drawings.find((d) => d.id === genSiFor.id) || genSiFor;
        return (
          <GenerateSiModal
            drawing={live}
            busy={siBusy}
            onYes={() => generateSI(live)}
            onNo={() => revertRev(live)}
            onClose={() => setGenSiFor(null)}
          />
        );
      })()}
    </div>
  );
}

export default React.memo(DrawingRegisterSection);
